import React, { useState, useEffect } from 'react';
import { websocketClient } from '../utils/websocketClient';

interface WebSocketScheduledPushProps {
  currentTheme: string;
  userId?: string;
}

interface TimerStatus {
  has_timer: boolean;
  is_running: boolean;
}

const WebSocketScheduledPush: React.FC<WebSocketScheduledPushProps> = ({ 
  currentTheme, 
  userId 
}) => {
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  const [timerStatus, setTimerStatus] = useState<TimerStatus>({ has_timer: false, is_running: false });
  const [intervalMinutes, setIntervalMinutes] = useState(30);
  const [message, setMessage] = useState('现在是 {time}，该继续你的项目了！');
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (userId && userId.trim() !== '') {
      initializeWebSocket();
    } else {
      // 如果没有有效的用户ID，确保断开连接
      websocketClient.disconnect();
      setConnectionStatus('disconnected');
    }

    return () => {
      // 组件卸载时断开WebSocket连接
      websocketClient.disconnect();
    };
  }, [userId]);

  const initializeWebSocket = async () => {
    console.log('WebSocket初始化 - userId:', userId);
    if (!userId) {
      console.log('WebSocket初始化 - 用户ID为空，跳过连接');
      return;
    }

    try {
      setConnectionStatus('connecting');
      await websocketClient.connect(userId);
      setConnectionStatus('connected');
      
      // 设置消息处理器
      websocketClient.on('timer_started', (data) => {
        setTimerStatus({ has_timer: true, is_running: true });
        setSuccess(`定时推送已启动，间隔: ${data.interval_minutes}分钟`);
        setError(null);
      });

      websocketClient.on('timer_stopped', (data) => {
        setTimerStatus({ has_timer: false, is_running: false });
        setSuccess('定时推送已停止');
        setError(null);
      });

      websocketClient.on('timer_updated', (data) => {
        setSuccess('定时推送设置已更新');
        setError(null);
      });

      websocketClient.on('status', (data) => {
        setTimerStatus(data.data);
      });

      websocketClient.on('scheduled_push', (data) => {
        setLastMessage(data.message);
        setSuccess(`收到定时推送: ${data.message}`);
      });

      websocketClient.on('test_push', (data) => {
        setLastMessage(data.message);
        setSuccess(`收到测试推送: ${data.message}`);
      });

      websocketClient.on('error', (data) => {
        setError(data.message);
        setSuccess(null);
      });

      // 获取当前状态
      websocketClient.getStatus();

    } catch (error) {
      console.error('WebSocket连接失败:', error);
      setConnectionStatus('disconnected');
      setError('WebSocket连接失败，请检查网络连接');
    }
  };

  const handleStart = () => {
    if (!websocketClient.isConnected()) {
      setError('WebSocket未连接，请先建立连接');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      websocketClient.startTimer({
        interval_minutes: intervalMinutes,
        message: message
      });
    } catch (error) {
      setError('启动定时推送失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = () => {
    if (!websocketClient.isConnected()) {
      setError('WebSocket未连接');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      websocketClient.stopTimer();
    } catch (error) {
      setError('停止定时推送失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = () => {
    if (!websocketClient.isConnected()) {
      setError('WebSocket未连接');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      websocketClient.sendTestPush('这是一条WebSocket测试推送消息');
      setSuccess('测试推送已发送');
    } catch (error) {
      setError('发送测试推送失败');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = () => {
    if (!websocketClient.isConnected()) {
      setError('WebSocket未连接');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      websocketClient.updateTimer({
        interval_minutes: intervalMinutes,
        message: message
      });
    } catch (error) {
      setError('更新定时推送失败');
    } finally {
      setLoading(false);
    }
  };

  const handleReconnect = () => {
    if (userId) {
      initializeWebSocket();
    }
  };

  const containerStyle = {
    maxWidth: '600px',
    margin: '20px auto',
    padding: '20px',
    backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : '#fff',
    border: `1px solid ${currentTheme === 'dark' ? '#434343' : '#d9d9d9'}`,
    borderRadius: '8px',
    color: currentTheme === 'dark' ? '#fff' : '#000'
  };

  const buttonStyle = {
    padding: '10px 20px',
    margin: '5px',
    border: 'none',
    borderRadius: '4px',
    cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    opacity: loading ? 0.6 : 1
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#1890ff',
    color: '#fff'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#ff4d4f',
    color: '#fff'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#52c41a',
    color: '#fff'
  };

  const warningButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#faad14',
    color: '#fff'
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    margin: '8px 0',
    border: `1px solid ${currentTheme === 'dark' ? '#434343' : '#d9d9d9'}`,
    borderRadius: '4px',
    backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#fff',
    color: currentTheme === 'dark' ? '#fff' : '#000'
  };

  const statusStyle = {
    padding: '10px',
    margin: '10px 0',
    borderRadius: '4px',
    fontSize: '14px'
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return '#52c41a';
      case 'connecting': return '#faad14';
      case 'disconnected': return '#ff4d4f';
      default: return '#999';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return '✅ 已连接';
      case 'connecting': return '🔄 连接中...';
      case 'disconnected': return '❌ 未连接';
      default: return '❓ 未知状态';
    }
  };

  return (
    <div style={containerStyle}>
      <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>
        🔌 WebSocket定时推送
      </h3>
      
      <p style={{ color: currentTheme === 'dark' ? '#ccc' : '#666', marginBottom: '20px' }}>
        基于WebSocket的实时定时推送，提供更好的用户体验和即时响应
      </p>

      {/* 连接状态 */}
      <div style={{ marginBottom: '20px' }}>
        <h4>连接状态</h4>
        <div style={{
          ...statusStyle,
          backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5',
          border: `1px solid ${getConnectionStatusColor()}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>WebSocket状态: {getConnectionStatusText()}</span>
            {connectionStatus === 'disconnected' && (
              <button
                onClick={handleReconnect}
                style={warningButtonStyle}
                disabled={loading}
              >
                重新连接
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 错误和成功消息 */}
      {error && (
        <div style={{
          ...statusStyle,
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          color: '#cf1322'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          ...statusStyle,
          backgroundColor: '#f6ffed',
          border: '1px solid #b7eb8f',
          color: '#389e0d'
        }}>
          {success}
        </div>
      )}

      {/* 最后收到的消息 */}
      {lastMessage && (
        <div style={{
          ...statusStyle,
          backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f0f8ff',
          border: '1px solid #91d5ff'
        }}>
          <strong>最后收到的消息:</strong> {lastMessage}
        </div>
      )}

      {/* 定时推送控制 */}
      {!timerStatus.is_running ? (
        <div>
          <h4>启动定时推送</h4>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              推送间隔（分钟）:
            </label>
            <input
              type="number"
              value={intervalMinutes}
              onChange={(e) => setIntervalMinutes(Number(e.target.value))}
              min="1"
              max="1440"
              style={inputStyle}
              placeholder="30"
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              推送消息:
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              style={inputStyle}
              placeholder="现在是 {time}，该继续你的项目了！"
              maxLength={200}
            />
          </div>

          <button
            onClick={handleStart}
            disabled={loading || connectionStatus !== 'connected'}
            style={primaryButtonStyle}
          >
            {loading ? '启动中...' : '🚀 启动WebSocket定时推送'}
          </button>
        </div>
      ) : (
        <div>
          <h4>当前定时推送状态</h4>
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5', borderRadius: '4px' }}>
            <p><strong>推送间隔：</strong>{intervalMinutes} 分钟</p>
            <p><strong>推送消息：</strong>{message}</p>
            <p><strong>状态：</strong>
              <span style={{ color: timerStatus.is_running ? '#52c41a' : '#ff4d4f' }}>
                {timerStatus.is_running ? '✅ 运行中' : '❌ 已暂停'}
              </span>
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={handleStop}
              disabled={loading || connectionStatus !== 'connected'}
              style={dangerButtonStyle}
            >
              {loading ? '停止中...' : '⏹️ 停止定时推送'}
            </button>
            
            <button
              onClick={handleUpdate}
              disabled={loading || connectionStatus !== 'connected'}
              style={secondaryButtonStyle}
            >
              {loading ? '更新中...' : '🔄 更新设置'}
            </button>
            
            <button
              onClick={handleTest}
              disabled={loading || connectionStatus !== 'connected'}
              style={warningButtonStyle}
            >
              {loading ? '测试中...' : '🧪 测试推送'}
            </button>
          </div>
        </div>
      )}

      {/* 功能说明 */}
      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5', borderRadius: '4px' }}>
        <h4>WebSocket定时推送优势</h4>
        <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
          <li>⚡ 实时响应，无延迟</li>
          <li>🔄 自动重连，连接稳定</li>
          <li>💬 双向通信，即时反馈</li>
          <li>🎯 精确控制，动态调整</li>
          <li>📱 移动端友好，省电省流量</li>
        </ul>
      </div>
    </div>
  );
};

export default WebSocketScheduledPush;
